<template>
  <LoadingZone :loading="loading" class="p-4">
    <h5 class="text-center bold mb-4">
      {{ $t("message.add_items_to") }}
      <span class="text-primary">
        {{ category.name }}
      </span>
      {{ $t("label.category") }}
    </h5>

    <input
      v-if="availableItems.length"
      v-model="search"
      class="form-control mb-3"
      type="text"
      :placeholder="
        $t('message.search_for_x_followed_by_ellipses', {
          search: $t('label.items'),
        })
      "
    />

    <ul class="p-0 m-0" style="max-height: calc(100vh - 320px); overflow: auto">
      <li
        v-for="(item, index) in filteredItems"
        :key="item.id"
        class="customCheckbox border-bottom"
      >
        <input
          :id="index"
          v-model="addItems"
          type="checkbox"
          class="customCheckbox__input"
          :value="item"
        />
        <label :for="index" class="customCheckbox__text py-2">
          <span class="customCheckbox__square"></span>
          <div class="row m-0">
            <div class="col p-0">
              <span :title="createdAtLabel(item.createdAt)">
                {{ item.name }}
              </span>
            </div>
            <div class="col-auto">
              <Money
                tag="small"
                :amount="item.amount"
                :business-id="businessId"
              />
            </div>
          </div>
        </label>
      </li>
    </ul>

    <div v-if="availableItems.length && !filteredItems.length" class="my-5">
      <p class="text-center">
        {{ $t("message.there_is_no_item_matching") }}
      </p>
    </div>

    <div v-if="!availableItems.length" class="my-5">
      <p class="text-center">
        {{ $t("message.there_is_no_item_attached") }}
      </p>
      <p class="text-center">
        <router-link
          class="btn btn-dark"
          :to="{ name: 'items.create' }"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ $t("label.create_item") }}
        </router-link>
      </p>
    </div>

    <div class="clearfix mt-4">
      <button class="float-left btn btn-light" @click="$emit('close')">
        {{ $t("label.cancel") }}
      </button>
      <button
        class="float-right btn btn-primary"
        :class="{ 'btn-progress': adding }"
        :disabled="!addItems.length"
        @click="save"
      >
        {{ $tc("label.add_x_items", addItems.length) }}
      </button>
    </div>
  </LoadingZone>
</template>

<script>
import Money from "@/components/helpers/Money.vue";
import LoadingZone from "@/components/helpers/LoadingZone.vue";
import Menus from "@/api/menus";
import Items from "@/api/items";
import { pluck, isEmpty, is, sortWith, ascend, prop } from "ramda";
import { mapGetters } from "vuex";
import { Toast } from "@/design-system";
import { date } from "@/utils/filters";

export default {
  name: "ItemsList",
  components: {
    LoadingZone,
    Money,
  },
  props: {
    itemsAdded: {
      type: Function,
      default: () => {},
    },
    excludeItems: {
      type: Array,
      default: null,
    },
    businessId: {
      type: String,
      required: true,
    },
    category: {
      type: Object,
      required: true,
    },
    sourceId: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    search: "",
    adding: false,
    loading: false,
    items: [],
    addItems: [],
  }),
  computed: {
    ...mapGetters({
      businesses: "businesses/getData",
    }),
    business() {
      return Object.values(this.businesses).find(
        (obj) => obj.id === this.businessId
      );
    },
    availableItems() {
      if (!is(Array, this.excludeItems)) return this.items;

      return this.items.filter((obj) => !this.excludeItems.includes(obj.id));
    },
    filteredItems() {
      if (isEmpty(this.search)) return this.availableItems;

      const query = this.search.trim().toLowerCase();
      return this.availableItems.filter((obj) =>
        obj.name.toLowerCase().includes(query)
      );
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    createdAtLabel(createdAt) {
      if (!createdAt) return;

      return `${this.$t("label.created_at")}: ${date(
        createdAt,
        "MMM DD, YYYY - h:mm A"
      )}`;
    },
    async fetch() {
      this.loading = true;

      const res = await Items.get(this.businessId, {
        is_modifier: 0,
        limit: 9999,
        source: this.sourceId,
      });
      const itemNameSort = sortWith([ascend(prop("name"))]);
      this.items = itemNameSort(res.data);

      this.loading = false;
    },
    async save() {
      try {
        this.adding = true;

        const res = await Menus.attachItemsToCategory(
          this.businessId,
          this.category.id,
          pluck("id", this.addItems)
        );

        this.$analytics.trackEvent("Added items to menu", {
          business_id: this.businessId,
          business_name: this.business.name,
          event_category: "menu_builder",
        });

        this.itemsAdded(res.items);
        this.$emit("close");
        new Toast().create({
          type: "success",
          text: this.$t("message.records_linked"),
        });
      } catch (e) {
        new Toast().create({
          type: "error",
          text: this.$t("message.generic_failure"),
        });
      } finally {
        this.adding = false;
      }
    },
  },
};
</script>
